import * as Sentry from '@sentry/react';
import { call, put, takeEvery, select, debounce, delay } from "redux-saga/effects";
import { putContentService } from "../../services/audio_composer";
import { showSavingInDraftLabel, updateCurrentAudioComposerBlock, addAudioComposerAudioBlock, initializeAudioComposerSuccess, updateComposerTTSBlocks, updateAudioComposerSections, toggleAudioComposerBlockActions } from "../actions";
import { APPLY_EDITOR_TITLE_SETTINGS, EXIT_EDITOR, PUT_BLOCK_AUDIO, UPDATE_AUDIO_COMPOSER_BLOCK, UPDATE_BLOCK_AUDIOS, UPDATE_SLATE_VALUE, ADD_AUDIO_COMPOSER_BLOCK, DUPLICATE_AUDIO_COMPOSER_BLOCK, UPDATE_TITLE, UPDATE_PRONUNCIATION_LIBRARY, UPDATE_AUDIO_COMPOSER_BLOCK_MOVE_LEFT, UPDATE_AUDIO_COMPOSER_BLOCK_MOVE_RIGHT, INITIALIZE_AUDIO_COMPOSER_REQUESTED, GENERATE_COMPOSER_SETTINGS, TOGGLE_INCLUDE_TITLE, UPDATE_VOICE_FOR_ALL_BLOCKS, UPDATE_CURRENT_AUDIO_COMPOSER_BLOCK } from "../actionTypes";
import { defaultBlockValue, defaultSectionsValue, defaultSlateBlockValue } from "../../components/composer/utils";
import hash from 'object-hash';
import { Transforms } from "slate";
import { getBlockDuration, getBlockLevelPronunciationLibrary, getBlockText } from "../../utils/SlateUtils/index.js";

// ✨ Zustand stores - Primary state management for voice
import { useVoiceSelectionStore } from "../../zustand-store/useVoiceSelectionStore";
import { useVoiceFiltersStore } from "../../zustand-store/useVoiceFiltersStore";
import { isDeprecatedAudioProfile } from "../../constants/deprecatedVoices";

function* saveDraftSaga(action) {
    try {
        const sections = yield select(state => state.audio_composer.sections);
        const slate_value = yield select(state => state.audio_composer.slate_value);
        const slateValueContentId = yield select(state => state.audio_composer.slateValueContentId);
        const isContentChanged = yield select(state => state.audio_composer.isContentChanged);
        const title = yield select(state => state.audio_composer.title);
        const content = yield select(state => state.edit_content.content);

        if (!content) {
            return;
        }

        const contentId = content.SK.split('#')[1];

        // slate_value can briefly lag content during a switch; don't save it under the wrong id
        if (slateValueContentId !== contentId) {
            Sentry.captureMessage('saveDraftSaga: skipped save, slate_value content id mismatch', {
                level: 'warning',
                extra: { contentId, slateValueContentId }
            });
            return;
        }

        // ✨ Get IDs from Zustand global store (set from URL)
        const { useGlobalStore } = require('../../zustand-store/useGlobalStore');
        const { orgId, integrationId } = useGlobalStore.getState();
        
        if (!orgId || !integrationId) {
            return;
        }

        yield put(showSavingInDraftLabel(true));
        
        yield call(putContentService, {
            orgId,
            integrationId,
            contentId,
            body: {
                title,
                slate_value,
                composer_settings : sections,
                is_draft: true,
                isContentChanged
            }
        });

        yield put(showSavingInDraftLabel(false));
    } catch (e) {
        console.log(e)
        Sentry.captureException(e);
    }

}

function* exitEditorSaga(action) {

    const { history } = action.payload;
    yield call(saveDraftSaga);
    yield delay(500);

    // ✨ Get org_id and integration_id from Zustand store instead of Redux
    const { useGlobalStore } = require('../../zustand-store/useGlobalStore');
    const { orgId, integrationId } = useGlobalStore.getState();

    if (!orgId || !integrationId) {
        console.warn('exitEditorSaga: Missing orgId or integrationId in Zustand store');
        return;
    }

    history.push(`/organisation/${orgId}/integration/${integrationId}/content`);
}

function* buildComposerBlocks(action) {

    // yield put(toggleCurrentAudioPlaying(false));

    // ✨ Get pronunciation library from React Query cache instead of Redux
    const { useGlobalStore } = require('../../zustand-store/useGlobalStore');
    const { orgId, integrationId } = useGlobalStore.getState();
    const { queryClient } = require('../../index');
    const integration = queryClient.getQueryData(['integration', orgId, integrationId]);
    const pronunciation_library = integration?.pronunciation_library || [];

    const slate_value = yield select(state => state.audio_composer.slate_value);
    const block_audios = yield select(state => state.audio_composer.block_audios);
    let composer_settings = yield select(state => state.audio_composer.sections);

    if (!slate_value || slate_value.length === 0) {
        return;
    }
    
    if (action.type === GENERATE_COMPOSER_SETTINGS) {

        composer_settings = defaultSectionsValue()

        const ttsBlocks = slate_value.map((block, blockIndex) => {
            const blockLevelPronunciationLibrary = getBlockLevelPronunciationLibrary(block, pronunciation_library)
            const blockHash = hash({
                ...block,
                pronunciation_library: blockLevelPronunciationLibrary
            })
    
            const blockAudio = block_audios[blockHash];
    
            const blockDuration = blockAudio ? blockAudio.duration : getBlockDuration(block)
        
            const ttsBlock = {
                ...defaultBlockValue(),
                ends : blockDuration, // In seconds relative to 0s
                trimRight: 0,
                url : blockAudio ? blockAudio.audio_url : null,
                duration: blockDuration, // Original duration of audio
                title: getBlockText(block)
            }
            
            return ttsBlock;
        })
    
        // yield put(updateComposerTTSBlocks(ttsBlocks));

        composer_settings[0].tracks[0].blocks = ttsBlocks;

        yield put(updateAudioComposerSections(composer_settings));

        return;
    }

    if (!composer_settings) {
        return;
    }

    if (composer_settings[0].tracks[0].blocks.length === 0) {
        return;
    }

    const ttsBlocks = slate_value.map((block, blockIndex) => {
        const blockLevelPronunciationLibrary = getBlockLevelPronunciationLibrary(block, pronunciation_library)
        const blockHash = hash({
            ...block,
            pronunciation_library: blockLevelPronunciationLibrary
        })

        const blockAudio = block_audios ? block_audios[blockHash] : null;

        const blockDuration = blockAudio ? blockAudio.duration : getBlockDuration(block)

        const isBlockAvailable = !!(composer_settings && composer_settings[0] && composer_settings[0].tracks[0] && composer_settings[0].tracks[0].blocks[blockIndex])

        const defaultBlockVal = isBlockAvailable ? (composer_settings[0].tracks[0].blocks[blockIndex]) : defaultBlockValue()

        let blockEnds, blockTrimRight;

        if (blockAudio && !defaultBlockVal.url) { // block audio just generated (reset crop)
            blockEnds = blockDuration
            blockTrimRight = 0
        } else if (!blockAudio) { // no block audio (reset crop)
            blockEnds = blockDuration
            blockTrimRight = 0;
        } else if (blockAudio) { // block audio and composer audio are equal (retain crop)
            // clamp in case the regenerated clip is shorter than the old crop
            blockEnds = Math.min(defaultBlockVal.ends, blockDuration)
            blockTrimRight = Math.max(0, blockDuration - blockEnds)
        } else { // (reset crop)
            blockEnds = blockDuration;
            blockTrimRight = 0;
        }

        const ttsBlock = {
            ...defaultBlockVal,
            ends : blockEnds, // In seconds relative to 0s
            trimRight: blockTrimRight,
            url : blockAudio ? blockAudio.audio_url : null,
            duration: blockDuration, // Original duration of audio
            title: getBlockText(block)
        }
        
        return ttsBlock;
    })

    yield put(updateComposerTTSBlocks(ttsBlocks))

    // composer_settings[0].tracks[0].blocks = ttsBlocks;

    // yield put(updateAudioComposerSections(composer_settings));

}

function* addAudioComposerBlockSaga(action) {
    const editor = yield select(state => state.audio_composer.editor);
    const slate_value = yield select(state => state.audio_composer.slate_value);
    let { sectionIndex, blockIndex } = action.payload;

    // ✨ Get integration from React Query cache
    const { useGlobalStore } = require('../../zustand-store/useGlobalStore');
    const { orgId, integrationId } = useGlobalStore.getState();
    const { queryClient } = require('../../index');
    const integration = queryClient.getQueryData(['integration', orgId, integrationId]);

    if(sectionIndex === 0) {
        
        let audio_profile = {
            ...integration.audio_profile,
            lang_code: integration.lang_code
        };

        if (!blockIndex) { // inseting a block at the end
            blockIndex = [slate_value.length]
        }
    
        if (blockIndex >= 1) {
            audio_profile = slate_value[blockIndex - 1].audio_profile;
        }
    
        Transforms.insertNodes(
            editor,
            defaultSlateBlockValue({
                ...audio_profile,
            }),
            {
                at: [blockIndex],
            }
        );

    } else {
        yield put(addAudioComposerAudioBlock(action.payload))
    }

}

function* duplicateAudioComposerBlockSaga(action) {
    const editor = yield select(state => state.audio_composer.editor);
    const slate_value = yield select(state => state.audio_composer.slate_value);
    const { sectionIndex, blockIndex } = action.payload;

    // Only section 0 (the text editor) supports duplication.
    if (sectionIndex !== 0) return;

    const sourceBlock = slate_value[blockIndex];
    if (!sourceBlock) return;

    // Deep clone so the duplicate is fully independent of the source block.
    const clonedBlock = JSON.parse(JSON.stringify(sourceBlock));

    // Only one block may be the title block (see services/editor.js) — a duplicate must not inherit it.
    delete clonedBlock.is_title;

    Transforms.insertNodes(
        editor,
        clonedBlock,
        {
            at: [blockIndex + 1],
        }
    );
}

function* postComposerBlockSwapHandler(action) {
    const { didSwap, blockIndex } = action.payload;

    if (!didSwap) {
        return;
    }

    yield delay(1)

    yield put(updateCurrentAudioComposerBlock({sectionIndex: 0, trackIndex: 0, blockIndex}))
    yield put(toggleAudioComposerBlockActions(false))
}

function* initializeAudioComposerSaga(action){
    yield put(initializeAudioComposerSuccess())
}

function* toggleIncludeTitleSaga(action) {

    const includeTitle = action.payload;
    const title = yield select(state => state.audio_composer.title);
    const slate_value = yield select(state => state.audio_composer.slate_value);
    const editor = yield select(state => state.audio_composer.editor);
    
    // ✨ Get integration from React Query cache
    const { useGlobalStore } = require('../../zustand-store/useGlobalStore');
    const { orgId, integrationId } = useGlobalStore.getState();
    const { queryClient } = require('../../index');
    const integration = queryClient.getQueryData(['integration', orgId, integrationId]);

    if (!slate_value || slate_value.length === 0) {
        return;
    }

    const isTitleAlreadyPresent = slate_value[0]?.is_title;

    yield delay(1)

    if (includeTitle && !isTitleAlreadyPresent) {
        let audio_profile = {
            ...integration.audio_profile,
            lang_code: integration.lang_code
        };

		Transforms.insertNodes(
            editor,
            {
				...defaultSlateBlockValue(audio_profile, title),
				is_title: true,
			},
            {
                at: [0],
            }
        );
    }

    if (!includeTitle && isTitleAlreadyPresent) {
        Transforms.removeNodes(editor, {at: [0]})
    }

}

// ✨ updateTitleInSlateValue saga removed - migrated to direct function in src/services/editor.js
// Now called via useEditorTitle hook which combines Redux state update + Slate editor update

function* updateVoiceForAllBlocksSaga(action) {
    const editor = yield select(state => state.audio_composer.editor);
    const slate_value = yield select(state => state.audio_composer.slate_value);

    if (!slate_value) return;

    const { isAudioProfile } = action.payload

    /**
     * @Info
     *  if the selected item is a audio profile
     */
    if (isAudioProfile) {

        const { lang_code, voice, engine, platform, volume, speed, pitch, profile_name } = action.payload;

        slate_value.forEach((_, blockIndex) => {
            Transforms.setNodes(
                editor,
                {
                    audio_profile: {
                        profile_name, pitch, speed, volume, voice, lang_code, engine, platform
                    }
                },
                {
                    at: [blockIndex]
                }
            )
        });

        return;
    }

    /**
     * @Info
     *  if the selected item is a voice and not audio profile
     */

    const {code: voice, engine, platform, langCode: lang_code, audio, metadata, qwen_pt_path } = action.payload;
    let newVoice = {
        engine,
        lang_code,
        platform,
        voice
    }

    // Add audio, metadata and qwen_pt_path for emotional voices
    if (engine === 'emotional') {
        if (audio) newVoice.audio = audio;
        if (metadata) newVoice.metadata = metadata;
        if (qwen_pt_path) newVoice.qwen_pt_path = qwen_pt_path;
    }

    slate_value.forEach((block, blockIndex) => {
        const oldAudioProfile = block.audio_profile;

        // Old profile was a saved profile
        // in that case, should unset audio_profile completely
        if (oldAudioProfile.profile_name) {
            Transforms.setNodes(
                editor,
                {
                    audio_profile: {
                        ...newVoice,
                        volume: 0,
                        speed: 100
                    }
                },
                {
                    at: [blockIndex]
                }
            )
        } else {
            Transforms.setNodes(
                editor,
                {
                    audio_profile: {
                        ...oldAudioProfile,
                        ...newVoice
                    }
                },
                {
                    at: [blockIndex]
                }
            )
        }
        
    })
}

// ✨ applyEditorTitleSettingsSaga removed - migrated to direct function in src/services/editor.js
// Now called directly from UpdateEditorTitleModal component

function* changeAudioProfileSettingsOfCurrentBlockData(action) {

    const selectedBlockPosition = action.payload;
    if (!selectedBlockPosition) {
        return;
    }

    const slateValue = yield select((state) => state.audio_composer.slate_value);
    const selectedBlock = slateValue[selectedBlockPosition.blockIndex];

    if (!selectedBlock) {
        return;
    }

    // ✨ Use Zustand store directly (Redux removed)
    const { setCurrentVoice, setCurrentLanguage, setAvailableVoices, setSelectedAudioProfile } = useVoiceSelectionStore.getState();
    const { setShowEditorVoiceProfiles } = useVoiceFiltersStore.getState();

    if (selectedBlock.audio_profile.profile_name) {
        setSelectedAudioProfile(selectedBlock.audio_profile);
        setShowEditorVoiceProfiles(true);
        setCurrentVoice(null);
    }

    // ✨ Languages are fetched via React Query, access from window or wait for component sync
    // For saga, we'll work with what's in selectedBlock directly
    const { lang_code: langCode, voice: voiceCode, engine } = selectedBlock.audio_profile;

    // start by disabling saved audio profile
    setSelectedAudioProfile(null);
    setShowEditorVoiceProfiles(false);

    // Get languages from React Query cache or Zustand if available
    // If not available, saga will skip voice selection (components handle it).
    // For deprecated providers (e.g. Azure), fall back to the legacy cache which
    // includes those voices — avoids silently selecting the wrong voice on block click.
    const currentLanguageStore = useVoiceSelectionStore.getState().currentLanguage;
    const isDeprecatedBlock = isDeprecatedAudioProfile(selectedBlock.audio_profile);
    const languages = (isDeprecatedBlock && window.__LANGUAGES_CACHE_LEGACY__)
        ? window.__LANGUAGES_CACHE_LEGACY__
        : window.__LANGUAGES_CACHE__ || {};

    if (!languages || !languages[langCode]) {
        return;
    }

    let currentVoices = languages[langCode].voices;
    let currentVoice = currentVoices.find((v) => v.engine === engine && v.code === voiceCode);

    // if no voice is a match for the current block, set the first voice of this language as the default voice
    if (!currentVoice) {
        currentVoice = currentVoices[0];
    }

    // ✨ Update Zustand only
    setCurrentVoice(currentVoice);
    if (langCode !== currentLanguageStore?.code) {
    setCurrentLanguage(languages[langCode]);
    setAvailableVoices(currentVoices);
    }
    
    // ✨ Voice capabilities (isPitchDisabled, etc.) are computed properties - no state updates needed
    // Update Zustand filters
    const { setLanguage: setFilterLanguage, setGender, setQuality } = useVoiceFiltersStore.getState();
    setFilterLanguage(langCode);
    setGender("all");
    setQuality("all");

}

function* audioComposerSaga() {
    yield debounce(1000, [UPDATE_SLATE_VALUE, UPDATE_TITLE, UPDATE_AUDIO_COMPOSER_BLOCK, ADD_AUDIO_COMPOSER_BLOCK, DUPLICATE_AUDIO_COMPOSER_BLOCK, APPLY_EDITOR_TITLE_SETTINGS], saveDraftSaga);
    yield takeEvery(EXIT_EDITOR, exitEditorSaga);

    yield takeEvery([
        UPDATE_SLATE_VALUE, 
        PUT_BLOCK_AUDIO, 
        UPDATE_BLOCK_AUDIOS, 
        UPDATE_PRONUNCIATION_LIBRARY,
        GENERATE_COMPOSER_SETTINGS
    ], buildComposerBlocks);

    yield takeEvery(ADD_AUDIO_COMPOSER_BLOCK, addAudioComposerBlockSaga);
    yield takeEvery(DUPLICATE_AUDIO_COMPOSER_BLOCK, duplicateAudioComposerBlockSaga);
    yield takeEvery([UPDATE_AUDIO_COMPOSER_BLOCK_MOVE_LEFT, UPDATE_AUDIO_COMPOSER_BLOCK_MOVE_RIGHT], postComposerBlockSwapHandler);
    yield takeEvery(INITIALIZE_AUDIO_COMPOSER_REQUESTED, initializeAudioComposerSaga);
    yield takeEvery(TOGGLE_INCLUDE_TITLE, toggleIncludeTitleSaga);
    // ✨ UPDATE_TITLE saga removed - now handled by useEditorTitle hook
    yield takeEvery(UPDATE_VOICE_FOR_ALL_BLOCKS, updateVoiceForAllBlocksSaga);
    // ✨ APPLY_EDITOR_TITLE_SETTINGS saga removed - now handled by direct function call in component

    yield takeEvery(UPDATE_CURRENT_AUDIO_COMPOSER_BLOCK, changeAudioProfileSettingsOfCurrentBlockData)
}

export default audioComposerSaga;